import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Stack,
    Typography,
    IconButton,
    TextField,
    Button,
    Tooltip,
    Paper,
    Divider,
    CircularProgress,
    Fade
} from '@material-ui/core';
import Close from '@material-ui/icons/Close';
import { useTheme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { LoadingButton, LocalizationProvider, MobileDatePicker, MobileDateRangePicker } from '@material-ui/lab';
import AdapterDateFns from '@material-ui/lab/AdapterDateFns';
import moment from 'moment';
import { formatDateOnly, dateOnlyMoment, dateOnlyToUnix } from 'src/utils/dateOnly';
import { useNavigate } from 'react-router';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import axios from 'axios';
import ProductsPopover from '../../components/ProductsPopover';
import ServicesPopover from '../../components/ServicesPopover';

import { STAGE_COLORS, STAGE_LABELS, usesEstimateSingleDateOnly } from './EstimateCalendar';

const getStageColor = (item) => STAGE_COLORS[item?.calendar_stage] || STAGE_COLORS.new;
const getStageLabel = (item) => STAGE_LABELS[item?.calendar_stage] || 'New';
const isRejectedEstimate = (item) =>
    item?.calendar_stage === 'Rejected' ||
    Number(item?.is_rejected) === 1 ||
    Number(item?.quotation_status) === 6;

export default function DateEstimatesPopup({
    open,
    onClose,
    selectedDate,
    estimates,
    onRefresh,
    embeddedInPcb = false,
}) {
    console.log('estimates+++++++++++++++++++++', estimates)
    const theme = useTheme();
    const navigate = useNavigate();
    const user = JSON.parse(window.localStorage.getItem('user'));
    const explodedPermissions = user?.exploded_permissions;
    const { enqueueSnackbar } = useSnackbar();

    const [hoveredItem, setHoveredItem] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [installDate, setInstallDate] = useState('');
    const [installStartDate, setInstallStartDate] = useState(null);
    const [installEndDate, setInstallEndDate] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [productsPopoverOpen, setProductsPopoverOpen] = useState(false);
    const [productsPopoverAnchor, setProductsPopoverAnchor] = useState(null);
    const [productsPopoverData, setProductsPopoverData] = useState({ productNames: '', eventColor: '#1ccaff' });
    const [servicesPopoverOpen, setServicesPopoverOpen] = useState(false);
    const [servicesPopoverAnchor, setServicesPopoverAnchor] = useState(null);
    const [servicesPopoverData, setServicesPopoverData] = useState({ serviceNames: '', eventColor: '#1ccaff' });

    useEffect(() => {
        if (editingItem) {
            if (editingItem.installation_end_date) {
                setInstallDate(formatDateOnly(editingItem.installation_end_date, 'YYYY-MM-DD'));
            } else if (editingItem.participation_ends) {
                setInstallDate(formatDateOnly(editingItem.participation_ends, 'YYYY-MM-DD'));
            } else {
                setInstallDate(formatDateOnly(editingItem.display_date || editingItem.start, 'YYYY-MM-DD'));
            }
            setInstallStartDate(
                editingItem.installation_start_date ? dateOnlyMoment(editingItem.installation_start_date).toDate() : null
            );
            setInstallEndDate(
                editingItem.installation_end_date ? dateOnlyMoment(editingItem.installation_end_date).toDate() : null
            );
        }
    }, [editingItem]);

    const handleUpdateEstimateDate = async (item) => {
        try {
            setIsUpdating(true);
            if (!explodedPermissions?.some((permission) => ['Manage Estimate Calendar'].includes(permission))) {
                enqueueSnackbar("You don't have necessary permission", { variant: 'error' });
                return;
            }

            const singleDateOnly = usesEstimateSingleDateOnly(item?.calendar_stage);
            const payload = {
                request_id: item.id,
                added_by: user.user_id,
            };

            if (singleDateOnly) {
                payload.participation_ends = dateOnlyToUnix(installDate);
                payload.installation_start_date = null;
                payload.installation_end_date = null;
            } else if (installStartDate && installEndDate) {
                payload.installation_start_date = dateOnlyToUnix(installStartDate);
                payload.installation_end_date = dateOnlyToUnix(installEndDate);
                payload.participation_ends = dateOnlyToUnix(installEndDate);
            } else if (installDate) {
                payload.participation_ends = dateOnlyToUnix(installDate);
                payload.installation_start_date = null;
                payload.installation_end_date = null;
            }

            const response = await axios.post(`${REST_API_END_POINT}request/update-estimate-calendar-date`, payload);

            if (response.data.status === 1) {
                enqueueSnackbar('Estimate date updated successfully', { variant: 'success' });
                setEditingItem(null);
                setInstallDate('');
                setInstallStartDate(null);
                setInstallEndDate(null);
                // Trigger refresh to update the data
                onRefresh();
            } else {
                enqueueSnackbar('Failed to update estimate date', { variant: 'error' });
            }
        } catch (error) {
            enqueueSnackbar('Failed to update estimate date', { variant: 'error' });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleItemClick = (item) => {
        if (!editingItem && user.user_type !== 3) {
            if (explodedPermissions?.some(permission =>
                ['View Estimate', 'Edit Estimate', 'View Estimate as Admin', 'View Estimate Jobs'].includes(permission)
            )) {
                navigate(PATH_DASHBOARD.quotation.viewRequest.replace(':id', item.id), {
                    state: { fromEstimateCalendar: true, ...(embeddedInPcb && { fromPcb: true }) },
                });
            } else {
                enqueueSnackbar('Access Denied: You do not have permission to view estimate', { variant: 'error' });
            }
        }
    };

    const handleEditClick = (e, item) => {
        e.stopPropagation();
        if (!explodedPermissions?.some((permission) => ['Manage Estimate Calendar'].includes(permission))) {
            enqueueSnackbar("You don't have necessary permission", { variant: 'error' });
            return;
        }
        setEditingItem(item);
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
        setInstallDate('');
        setInstallStartDate(null);
        setInstallEndDate(null);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: '80vh',
                    maxHeight: '80vh',
                    minHeight: '400px',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">
                        Estimates for {selectedDate ? moment(selectedDate).format('MMMM DD, YYYY') : ''} ({estimates?.length || 0})
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Stack>
            </DialogTitle>
            <Divider sx={{ mt: 2 }} />
            <DialogContent dividers sx={{ flex: 1, overflow: 'hidden', p: 0 }}>
                <Box sx={{ height: '100%', overflowY: 'auto', p: 2 }}>
                    {estimates && estimates.length > 0 ? (
                        <Stack spacing={2}>
                            {estimates.map((item, index) => (
                                <Fade in timeout={300 + (index * 100)} key={item.id}>
                                    <Paper
                                        elevation={hoveredItem === item.id ? 4 : 1}
                                        sx={{
                                            p: 2,
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            border: `2px solid ${hoveredItem === item.id ? getStageColor(item) : '#233940'}`,
                                            backgroundColor: hoveredItem === item.id ? `${getStageColor(item)}10` : 'background.paper',
                                            '&:hover': {
                                                transform: 'translateY(-2px)',
                                                boxShadow: theme.shadows[4]
                                            }
                                        }}
                                        onMouseEnter={() => setHoveredItem(item.id)}
                                        onMouseLeave={() => setHoveredItem(null)}
                                        onClick={() => !editingItem && handleItemClick(item)}
                                    >
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                            <Box sx={{ flex: 1 }}>
                                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                                    <Box
                                                        sx={{
                                                            width: 12,
                                                            height: 12,
                                                            borderRadius: '50%',
                                                            backgroundColor: getStageColor(item)
                                                        }}
                                                    />
                                                    <Typography variant="subtitle1" fontWeight="bold">
                                                        {item.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            px: 1,
                                                            py: 0.5,
                                                            borderRadius: 1,
                                                            backgroundColor: `${getStageColor(item)}20`,
                                                            color: getStageColor(item),
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {getStageLabel(item)}
                                                    </Typography>
                                                </Stack>

                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {item.description}
                                                </Typography>

                                                <Stack direction="row" spacing={3} sx={{ mb: 1, flexWrap: 'wrap' }}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Start Date:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                                                            {formatDateOnly(item.start_date)}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Due Date:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                                                            {moment.utc(item.end).subtract(1, 'day').format("MM/DD/YYYY")}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {usesEstimateSingleDateOnly(item.calendar_stage) ? 'Estimate Date:' : 'Installation Date:'}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                                                            {item.installation_start_date && item.installation_end_date
                                                                ? `${formatDateOnly(item.installation_start_date)} - ${formatDateOnly(item.installation_end_date)}`
                                                                : item.participation_ends
                                                                    ? formatDateOnly(item.participation_ends)
                                                                    : formatDateOnly(item.display_date || item.start)}
                                                        </Typography>
                                                    </Box>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            QB Number:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                                                            {item.doc_number ? `#${item.doc_number}` : 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Stack>

                                                <Stack direction="row" spacing={2}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Client:
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                                                            {item.customer_name || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                    {item.community_name && (
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Project:
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
                                                                {item.community_name}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    {item.product_name && (
                                                        <Box sx={{ mt: 1 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                                Product{item.product_name?.includes(',') ? 's' : ''}:
                                                            </Typography>
                                                            {(() => {
                                                                const products = item.product_name ? item.product_name.split(',').map(p => p.trim()).filter(p => p) : [];
                                                                const eventColor = getStageColor(item);
                                                                
                                                                if (products.length <= 2) {
                                                                    return (
                                                                        <Box
                                                                            sx={{
                                                                                backgroundColor: `${eventColor}15`,
                                                                                border: `1px solid ${eventColor}40`,
                                                                                borderRadius: '4px',
                                                                                padding: '6px 8px',
                                                                                minHeight: '30px'
                                                                            }}
                                                                        >
                                                                            <Stack spacing={0.5}>
                                                                                {products.map((product, index) => (
                                                                                    <Typography
                                                                                        key={index}
                                                                                        color={eventColor}
                                                                                        fontSize="0.7rem"
                                                                                        sx={{
                                                                                            lineHeight: 1.2,
                                                                                            opacity: 0.9,
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                            whiteSpace: 'nowrap',
                                                                                            display: 'block'
                                                                                        }}
                                                                                        variant="caption"
                                                                                    >
                                                                                        • {product}
                                                                                    </Typography>
                                                                                ))}
                                                                            </Stack>
                                                                </Box>
                                                                    );
                                                                }
                                                                
                                                                // Show first 2 products + count more
                                                                const firstTwoProducts = products.slice(0, 2);
                                                                const remainingCount = products.length - 2;
                                                                
                                                                return (
                                                                    <Box
                                                                        sx={{
                                                                            backgroundColor: `${eventColor}15`,
                                                                            border: `1px solid ${eventColor}40`,
                                                                            borderRadius: '4px',
                                                                            padding: '6px 8px',
                                                                            minHeight: '30px',
                                                                            cursor: 'pointer',
                                                                            '&:hover': {
                                                                                backgroundColor: `${eventColor}25`,
                                                                                border: `1px solid ${eventColor}60`
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Stack spacing={0.5}>
                                                                            {firstTwoProducts.map((product, index) => (
                                                                                <Typography
                                                                                    key={index}
                                                                                    color={eventColor}
                                                                                    fontSize="0.7rem"
                                                                                    sx={{
                                                                                        lineHeight: 1.2,
                                                                                        opacity: 0.9,
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap',
                                                                                        display: 'block'
                                                                                    }}
                                                                                    variant="caption"
                                                                                >
                                                                                    • {product}
                                                                                </Typography>
                                                                            ))}
                                                                            <Typography
                                                                                color={eventColor}
                                                                                fontSize="0.7rem"
                                                                                sx={{
                                                                                    lineHeight: 1.2,
                                                                                    opacity: 0.9,
                                                                                    fontWeight: 600,
                                                                                    display: 'block',
                                                                                    cursor: 'pointer',
                                                                                    '&:hover': {
                                                                                        opacity: 1,
                                                                                        textDecoration: 'underline'
                                                                                    }
                                                                                }}
                                                                                variant="caption"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setProductsPopoverData({
                                                                                        productNames: item.product_name,
                                                                                        eventColor: eventColor
                                                                                    });
                                                                                    setProductsPopoverAnchor(e.currentTarget);
                                                                                    setProductsPopoverOpen(true);
                                                                                }}
                                                                            >
                                                                                +{remainingCount} more
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Box>
                                                                );
                                                            })()}
                                                        </Box>
                                                    )}
                                                    {item.service_name && (
                                                        <Box sx={{ mt: 1 }}>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                                Service{item.service_name?.includes(',') ? 's' : ''}:
                                                            </Typography>
                                                            {(() => {
                                                                const services = item.service_name ? item.service_name.split(',').map(s => s.trim()).filter(s => s) : [];
                                                                const eventColor = getStageColor(item);
                                                                
                                                                if (services.length <= 2) {
                                                                    return (
                                                                        <Box
                                                                            sx={{
                                                                                backgroundColor: `${eventColor}15`,
                                                                                border: `1px solid ${eventColor}40`,
                                                                                borderRadius: '4px',
                                                                                padding: '6px 8px',
                                                                                minHeight: '30px'
                                                                            }}
                                                                        >
                                                                            <Stack spacing={0.5}>
                                                                                {services.map((service, index) => (
                                                                                    <Typography
                                                                                        key={index}
                                                                                        color={eventColor}
                                                                                        fontSize="0.7rem"
                                                                                        sx={{
                                                                                            lineHeight: 1.2,
                                                                                            opacity: 0.9,
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                            whiteSpace: 'nowrap',
                                                                                            display: 'block'
                                                                                        }}
                                                                                        variant="caption"
                                                                                    >
                                                                                        • {service}
                                                                                    </Typography>
                                                                                ))}
                                                                            </Stack>
                                                                        </Box>
                                                                    );
                                                                }
                                                                
                                                                // Show first 2 services + count more
                                                                const firstTwoServices = services.slice(0, 2);
                                                                const remainingCount = services.length - 2;
                                                                
                                                                return (
                                                                    <Box
                                                                        sx={{
                                                                            backgroundColor: `${eventColor}15`,
                                                                            border: `1px solid ${eventColor}40`,
                                                                            borderRadius: '4px',
                                                                            padding: '6px 8px',
                                                                            minHeight: '30px',
                                                                            cursor: 'pointer',
                                                                            '&:hover': {
                                                                                backgroundColor: `${eventColor}25`,
                                                                                border: `1px solid ${eventColor}60`
                                                                            }
                                                                        }}
                                                                    >
                                                                        <Stack spacing={0.5}>
                                                                            {firstTwoServices.map((service, index) => (
                                                                                <Typography
                                                                                    key={index}
                                                                                    color={eventColor}
                                                                                    fontSize="0.7rem"
                                                                                    sx={{
                                                                                        lineHeight: 1.2,
                                                                                        opacity: 0.9,
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap',
                                                                                        display: 'block'
                                                                                    }}
                                                                                    variant="caption"
                                                                                >
                                                                                    • {service}
                                                                                </Typography>
                                                                            ))}
                                                                            <Typography
                                                                                color={eventColor}
                                                                                fontSize="0.7rem"
                                                                                sx={{
                                                                                    lineHeight: 1.2,
                                                                                    opacity: 0.9,
                                                                                    fontWeight: 600,
                                                                                    display: 'block',
                                                                                    cursor: 'pointer',
                                                                                    '&:hover': {
                                                                                        opacity: 1,
                                                                                        textDecoration: 'underline'
                                                                                    }
                                                                                }}
                                                                                variant="caption"
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    setServicesPopoverData({
                                                                                        serviceNames: item.service_name,
                                                                                        eventColor: eventColor
                                                                                    });
                                                                                    setServicesPopoverAnchor(e.currentTarget);
                                                                                    setServicesPopoverOpen(true);
                                                                                }}
                                                                            >
                                                                                +{remainingCount} more
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Box>
                                                                );
                                                            })()}
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </Box>

                                            {hoveredItem === item.id && !editingItem && !isRejectedEstimate(item) && (
                                                <Fade in>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => handleEditClick(e, item)}
                                                        sx={{
                                                            borderColor: getStageColor(item),
                                                            color: getStageColor(item),
                                                            '&:hover': {
                                                                backgroundColor: `${getStageColor(item)}20`
                                                            }
                                                        }}
                                                    >
                                                        {usesEstimateSingleDateOnly(item.calendar_stage) ? 'Edit Estimate Date' : 'Edit Installation Date'}
                                                    </Button>
                                                </Fade>
                                            )}
                                        </Stack>

                                        {editingItem && editingItem.id === item.id && !isRejectedEstimate(item) && (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    p: 2,
                                                    backgroundColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50',
                                                    borderRadius: 1,
                                                    border: `1px solid ${theme.palette.divider}`
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    {usesEstimateSingleDateOnly(item.calendar_stage) ? 'Update Estimate Date' : 'Update Installation Date'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                                                    {usesEstimateSingleDateOnly(item.calendar_stage)
                                                        ? 'Select the estimate scheduled date'
                                                        : 'Choose either single installation date OR installation date range'}
                                                </Typography>
                                                <Stack spacing={2}>
                                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                        <MobileDatePicker
                                                            orientation="portrait"
                                                            label={usesEstimateSingleDateOnly(item.calendar_stage) ? 'Estimate Date' : 'Installation Date'}
                                                            value={installDate ? new Date(installDate) : null}
                                                            onChange={(newValue) => {
                                                                if (newValue) {
                                                                    setInstallDate(moment(newValue).format('YYYY-MM-DD'));
                                                                    setInstallStartDate(null);
                                                                    setInstallEndDate(null);
                                                                }
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    fullWidth
                                                                    size="small"
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        sx: {
                                                                            '& input': {
                                                                                color: theme.palette.primary.main,
                                                                                fontSize: '0.875rem',
                                                                            },
                                                                        },
                                                                    }}
                                                                    InputLabelProps={{ shrink: true }}
                                                                />
                                                            )}
                                                        />
                                                    </LocalizationProvider>
                                                    {!usesEstimateSingleDateOnly(item.calendar_stage) ? (
                                                        <Stack direction="row" spacing={2} alignItems="center">
                                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                <MobileDatePicker
                                                                    orientation="portrait"
                                                                    label="Installation Start Date"
                                                                    value={installStartDate}
                                                                    onChange={(newValue) => {
                                                                        if (newValue) {
                                                                            setInstallStartDate(newValue);
                                                                            setInstallDate('');
                                                                            if (installEndDate && moment(newValue).isAfter(moment(installEndDate))) {
                                                                                setInstallEndDate(null);
                                                                            }
                                                                        }
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            size="small"
                                                                            sx={{ flex: 1, minWidth: 200 }}
                                                                            InputProps={{
                                                                                ...params.InputProps,
                                                                                sx: {
                                                                                    '& input': {
                                                                                        color: theme.palette.primary.main,
                                                                                        fontSize: '0.875rem',
                                                                                    },
                                                                                },
                                                                            }}
                                                                            InputLabelProps={{ shrink: true }}
                                                                        />
                                                                    )}
                                                                />
                                                            </LocalizationProvider>
                                                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                                <MobileDatePicker
                                                                    orientation="portrait"
                                                                    label="Installation End Date"
                                                                    value={installEndDate}
                                                                    minDate={installStartDate || new Date()}
                                                                    onChange={(newValue) => {
                                                                        if (newValue) {
                                                                            setInstallEndDate(newValue);
                                                                            setInstallDate(moment(newValue).format('YYYY-MM-DD'));
                                                                        }
                                                                    }}
                                                                    renderInput={(params) => (
                                                                        <TextField
                                                                            {...params}
                                                                            size="small"
                                                                            sx={{ flex: 1, minWidth: 200 }}
                                                                            InputProps={{
                                                                                ...params.InputProps,
                                                                                sx: {
                                                                                    '& input': {
                                                                                        color: theme.palette.primary.main,
                                                                                        fontSize: '0.875rem',
                                                                                    },
                                                                                },
                                                                            }}
                                                                            InputLabelProps={{ shrink: true }}
                                                                        />
                                                                    )}
                                                                />
                                                            </LocalizationProvider>
                                                        </Stack>
                                                    ) : null}
                                                    <LoadingButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleUpdateEstimateDate(item);
                                                        }}
                                                        loading={isUpdating}
                                                        variant="contained"
                                                        size="small"
                                                        disabled={
                                                            usesEstimateSingleDateOnly(item.calendar_stage)
                                                                ? !installDate
                                                                : !installDate && !(installStartDate && installEndDate)
                                                        }
                                                    >
                                                        Save
                                                    </LoadingButton>
                                                    <Button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCancelEdit();
                                                        }}
                                                        variant="outlined"
                                                        size="small"
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Stack>
                                            </Box>
                                        )}
                                    </Paper>
                                </Fade>
                            ))}
                        </Stack>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" color="text.secondary">
                                No estimates found for this date
                            </Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            
            <ProductsPopover
                productNames={productsPopoverData.productNames}
                open={productsPopoverOpen}
                anchorEl={productsPopoverAnchor}
                onClose={() => setProductsPopoverOpen(false)}
                eventColor={productsPopoverData.eventColor}
            />
            
            <ServicesPopover
                serviceNames={servicesPopoverData.serviceNames}
                open={servicesPopoverOpen}
                anchorEl={servicesPopoverAnchor}
                onClose={() => setServicesPopoverOpen(false)}
                eventColor={servicesPopoverData.eventColor}
            />
        </Dialog>
    );
}
